import {
  EMOTION_AXES,
  framingEmotionContribution,
  stickerComboForPlacement,
  type CaptionPlacementZone,
  type EmotionAxis,
  type EmotionVector,
  type LandingConfig,
} from "../../game/emotion-model.js";
import type { CaptionAuthoredStyle, ResolvedEdit } from "@desk/core";
import type { HeadlineFraming } from "../../content/creative-catalogs.js";

/**
 * Compact "what this framing does to the landing" chips for the Caption tool.
 *
 * DERIVED, not authored: emotion impacts come straight from the framing's
 * contribution in `framingEmotionContribution` (the same numbers
 * `editEmotionVector` folds into "How it's landing"); the cred/receipts impact
 * comes from the framing's `risk` flag combined with its Doubt exposure
 * (checkable claim that leaves receipts = cred down; honest/warm with no exposure
 * = cred up; deniable spin = cred untouched); reach follows the outrage charge.
 * Presentation only; never feeds scoring.
 */
export type ImpactTone =
  | "anger"
  | "alarm"
  | "pity"
  | "doubt"
  | "cred-bad"
  | "cred-good"
  | "reach";

export interface FramingImpact {
  /** Stable key for testids / React keys (e.g. "anger", "cred"). */
  key: string;
  /** Short metric label shown on the chip. */
  label: string;
  /** Arrow direction. */
  dir: "up" | "down";
  /** 1 = single arrow, 2 = double arrow (strong move). */
  strength: 1 | 2;
  tone: ImpactTone;
}

const AXIS_CHIPS: {
  axis: "Rage" | "Alarm" | "Pity" | "Doubt";
  /** Stable key for testids / React keys (kept as "anger" for the Rage axis). */
  key: string;
  label: string;
  tone: ImpactTone;
}[] = [
  // User-facing label matches the spider axis ("Rage"); the key stays "anger"
  // so testids / display order are unaffected.
  { axis: "Rage", key: "anger", label: "Rage", tone: "anger" },
  { axis: "Alarm", key: "alarm", label: "alarm", tone: "alarm" },
  { axis: "Pity", key: "pity", label: "pity", tone: "pity" },
  { axis: "Doubt", key: "doubt", label: "doubt", tone: "doubt" },
];

const AXIS_THRESHOLD = 0.12;

/**
 * Fixed DISPLAY order so the same metric always sits in the same slot across
 * framings (selection of which chips show is still by magnitude below; only the
 * left-to-right order is pinned). Alarm always precedes anger.
 */
const DISPLAY_ORDER = ["cred", "pity", "alarm", "anger", "doubt", "warm", "funny", "reach"];
function displayRank(key: string): number {
  const i = DISPLAY_ORDER.indexOf(key);
  return i === -1 ? DISPLAY_ORDER.length : i;
}

function strengthFor(v: number): 1 | 2 {
  return v >= 0.3 ? 2 : 1;
}

/** Arrow glyphs for a chip (no em-dashes; plain up/down arrows). */
export function impactArrow(impact: FramingImpact): string {
  const glyph = impact.dir === "up" ? "↑" : "↓";
  return impact.strength === 2 ? glyph + glyph : glyph;
}

/** The 2-3 most notable landing impacts of a headline framing. */
export function framingImpacts(framing: HeadlineFraming, config: LandingConfig): FramingImpact[] {
  const e = framingEmotionContribution(framing.tokenIds, config);
  const doubt = e.Doubt;
  const warm = e.Warm;
  const candidates: (FramingImpact & { note: number })[] = [];

  // Cred / receipts — the core lesson. Checkable claim that leaves Doubt exposure
  // dings cred; an honest, warm read with no exposure builds it.
  let credDown = false;
  if (framing.risk === "checkable" && doubt >= 0.2) {
    credDown = true;
    candidates.push({
      key: "cred",
      label: "cred",
      dir: "down",
      strength: strengthFor(doubt),
      tone: "cred-bad",
      note: doubt + 0.05,
    });
  } else if (warm >= 0.2 && doubt < 0.05) {
    candidates.push({
      key: "cred",
      label: "cred",
      dir: "up",
      strength: 1,
      tone: "cred-good",
      note: warm + 0.05,
    });
  }

  // Emotion axes actually raised by this framing.
  for (const { axis, key, label, tone } of AXIS_CHIPS) {
    const v = e[axis];
    if (v < AXIS_THRESHOLD) continue;
    // Cred-down already expresses the Doubt exposure; avoid double-counting it.
    if (key === "doubt" && credDown) continue;
    candidates.push({
      key,
      label,
      dir: "up",
      strength: strengthFor(v),
      tone,
      note: v,
    });
  }

  // Reach follows the outrage charge; honest/neutral framings barely travel.
  const outrage = e.Rage + e.Alarm;
  const charge = outrage + e.Pity + doubt;
  if (outrage >= 0.4) {
    candidates.push({
      key: "reach",
      label: "reach",
      dir: "up",
      strength: outrage >= 0.6 ? 2 : 1,
      tone: "reach",
      // Weighted below real emotion axes so it reads as a secondary effect.
      note: outrage * 0.4,
    });
  } else if (charge < 0.2) {
    candidates.push({
      key: "reach",
      label: "reach",
      dir: "down",
      strength: 1,
      tone: "reach",
      note: 0.15,
    });
  }

  return candidates
    .sort((a, b) => b.note - a.note) // pick the 3 most notable
    .slice(0, 3)
    .sort((a, b) => displayRank(a.key) - displayRank(b.key)) // then show them in a fixed order
    .map(({ note: _note, ...impact }) => impact);
}

/** Chip metadata for every axis a caption STYLE can move (drives `styleImpacts`). */
const STYLE_AXIS_CHIPS: Record<EmotionAxis, { key: string; label: string; tone: ImpactTone }> = {
  Rage: { key: "anger", label: "Rage", tone: "anger" },
  Alarm: { key: "alarm", label: "alarm", tone: "alarm" },
  Pity: { key: "pity", label: "pity", tone: "pity" },
  Doubt: { key: "doubt", label: "doubt", tone: "doubt" },
  Funny: { key: "funny", label: "funny", tone: "reach" },
  Warm: { key: "warm", label: "warm", tone: "cred-good" },
};

/**
 * A raise in Doubt at or above this reads as "leaves receipts" → a cred DING.
 * Kept low so the tradeoff surfaces on any control that meaningfully raises Doubt
 * (looks, effects, jump-cuts), matching the CLEAN_DOUBT lesson.
 */
const CRED_DOUBT_MIN = 0.12;

export interface SpecImpactOptions {
  /** Emit a reach chip from the outrage charge (captions only). Default false. */
  reach?: boolean;
  /** Derive the cred receipts chip from Doubt / Warm. Default true. */
  cred?: boolean;
  /** Max chips returned. Default 3. */
  limit?: number;
}

/**
 * The UNIFIED "this does that" chip builder: turn ANY emotion contribution spec
 * (a `Partial<EmotionVector>` straight off the scenario's `levers`) into the
 * 2–3 most notable landing-impact chips, in the same visual language as the
 * Caption tool. It surfaces the TRADEOFF unmissably — a spec that raises Doubt
 * shows a `cred ↓` chip (you leave receipts) ALONGSIDE the outrage axes it
 * raises; a warm spec with no Doubt shows `cred ↑`. Every consumer derives its
 * chips from the SAME `levers` the emotion model / `computeAudienceResult` fold
 * into the landing, so the chip promise equals what publishing does.
 * Presentation only; never feeds scoring.
 */
export function emotionSpecImpacts(
  spec: Partial<EmotionVector> | undefined,
  opts: SpecImpactOptions = {},
): FramingImpact[] {
  if (!spec) return [];
  const { reach = false, cred = true, limit = 3 } = opts;
  const get = (a: EmotionAxis) => spec[a] ?? 0;
  const doubt = get("Doubt");
  const warm = get("Warm");
  const candidates: (FramingImpact & { note: number })[] = [];

  // Cred / receipts — the core lesson. A Doubt raise dings cred; a warm read with
  // no Doubt builds it. When shown, the raw Doubt / Warm axis chip is suppressed
  // (cred already expresses it) to keep the row compact and non-redundant.
  let credDown = false;
  let credUp = false;
  if (cred) {
    if (doubt >= CRED_DOUBT_MIN) {
      credDown = true;
      candidates.push({
        key: "cred",
        label: "cred",
        dir: "down",
        strength: strengthFor(doubt),
        tone: "cred-bad",
        note: doubt + 0.05,
      });
    } else if (warm >= 0.2 && doubt < 0.05) {
      credUp = true;
      candidates.push({
        key: "cred",
        label: "cred",
        dir: "up",
        strength: 1,
        tone: "cred-good",
        note: warm + 0.05,
      });
    }
  }

  for (const axis of EMOTION_AXES) {
    const v = get(axis);
    if (v < AXIS_THRESHOLD) continue;
    if (axis === "Doubt" && credDown) continue;
    if (axis === "Warm" && credUp) continue;
    const chip = STYLE_AXIS_CHIPS[axis];
    candidates.push({
      key: chip.key,
      label: chip.label,
      dir: "up",
      strength: strengthFor(v),
      tone: chip.tone,
      note: v,
    });
  }

  if (reach) {
    const outrage = get("Rage") + get("Alarm");
    if (outrage >= 0.4) {
      candidates.push({
        key: "reach",
        label: "reach",
        dir: "up",
        strength: outrage >= 0.6 ? 2 : 1,
        tone: "reach",
        note: outrage * 0.4,
      });
    }
  }

  return candidates
    .sort((a, b) => b.note - a.note)
    .slice(0, limit)
    .sort((a, b) => displayRank(a.key) - displayRank(b.key))
    .map(({ note: _note, ...impact }) => impact);
}

/** Build a representative emotion spec from scaled `contributions` (uses caps). */
function specFromContributions(
  contributions: readonly { axis: EmotionAxis; per: number; cap: number }[],
): Partial<EmotionVector> {
  const spec: Partial<EmotionVector> = {};
  for (const c of contributions) spec[c.axis] = Math.max(spec[c.axis] ?? 0, c.cap);
  return spec;
}

/**
 * The 2-3 most notable landing impacts of a caption STYLE, derived from the
 * scenario config's `levers.captionStyles[style]` — the SAME numbers
 * `editEmotionVector` folds in. A style with no config entry (documentary)
 * returns no chips (reads as "minimal"). Presentation only; never feeds scoring.
 */
export function styleImpacts(style: CaptionAuthoredStyle, config: LandingConfig): FramingImpact[] {
  return emotionSpecImpacts(config.levers.captionStyles[style]);
}

/**
 * Landing impacts of a LOOK filter (single color grade), resolved exactly as
 * `editEmotionVector` resolves it: an exact `looks.byId[id]` entry first, else
 * the first matching keyword group. Cosmetic grades (no lever) → no chips.
 */
export function lookFilterImpacts(treatmentId: string, config: LandingConfig): FramingImpact[] {
  const id = treatmentId.toLowerCase();
  const looks = config.levers.looks;
  const byId = looks.byId[id];
  if (byId) return emotionSpecImpacts(byId);
  for (const lever of looks.groups) {
    if (lever.match.some((k) => id.includes(k))) return emotionSpecImpacts(lever.emotion);
  }
  return [];
}

/**
 * Landing impacts of a stackable EFFECT (rain / lightning / focus-blur), from
 * `levers.effects[id]`. A purely cosmetic effect (no lever entry) → no chips.
 */
export function effectImpacts(effectId: string, config: LandingConfig): FramingImpact[] {
  const effects = config.levers.effects;
  if (!effects) return [];
  return emotionSpecImpacts(effects[effectId.toLowerCase()] ?? effects[effectId]);
}

/**
 * Landing impacts of a SOUND treatment, resolved through `levers.sounds` with the
 * SAME first-match rule `editEmotionVector` uses (role match OR asset-id keyword).
 * Silence / an unmatched bed → no chips ("minimal").
 */
export function soundImpacts(
  sound: { assetId?: string | null; role?: string | null },
  config: LandingConfig,
): FramingImpact[] {
  const id = String(sound.assetId ?? "").toLowerCase();
  const role = sound.role != null ? String(sound.role) : undefined;
  for (const lever of config.levers.sounds) {
    const roleHit = role !== undefined && (lever.roles?.includes(role) ?? false);
    const keyHit = id.length > 0 && lever.match.some((k) => id.includes(k));
    if (roleHit || keyHit) return emotionSpecImpacts(lever.emotion);
  }
  return [];
}

/**
 * Landing impacts of a STICKER, resolved through `levers.stickerZones` +
 * `levers.stickerCombos` exactly as `editEmotionVector` resolves them: only
 * when THIS sticker id lands inside the zone an authored combo pairs it with,
 * AND its appear-window overlaps that zone's beat, do we read that combo's
 * emotion; with no anchor, or an anchor/window that matches no combo for this
 * sticker (wrong zone, a zone with no combo for this sticker, or on-zone but
 * outside the zone's beat), the placement is purely cosmetic (no chips).
 */
export function stickerImpacts(
  stickerId: string,
  config: LandingConfig,
  anchor?: { xMilli: number; yMilli: number; startTick: number; endTick: number },
): FramingImpact[] {
  if (!anchor) return [];
  const combo = stickerComboForPlacement(
    stickerId,
    anchor.xMilli,
    anchor.yMilli,
    anchor.startTick,
    anchor.endTick,
    config.levers.stickerZones,
    config.levers.stickerCombos,
  );
  if (!combo) return [];
  return emotionSpecImpacts(combo.emotion);
}

/**
 * Landing impacts of CUTTING context (omitting reassuring beats), from
 * `levers.contextCut.contributions`. Shown as a static "what cutting does" hint
 * so the Rage/Doubt cost of an incomplete story reads at a glance.
 */
export function contextCutImpacts(config: LandingConfig): FramingImpact[] {
  return emotionSpecImpacts(specFromContributions(config.levers.contextCut.contributions));
}

/**
 * The Doubt cost of visible JUMP-CUTS (reordering / non-contiguous cuts), from
 * `levers.jumpCut`. Surfaced as a `cred ↓` receipts chip so the "looks edited"
 * tradeoff of chopping the timeline is legible.
 */
export function jumpCutImpacts(config: LandingConfig): FramingImpact[] {
  return emotionSpecImpacts({ Doubt: config.levers.jumpCut.cap });
}

/**
 * Landing impacts of a TIGHT crop that isolates the subject, from
 * `levers.focusCrop.contributions`. Shown on the Focus tool so the Doubt/Rage
 * cost of hiding the surrounding context reads at a glance.
 */
export function focusCropImpacts(config: LandingConfig): FramingImpact[] {
  return emotionSpecImpacts(specFromContributions(config.levers.focusCrop.contributions));
}

// ── "What you changed" — the applied-edit changelog with impacts ──────────────

/** One applied edit + its emotion impact, for the "What you changed" list. */
export interface EditChangeRow {
  key: string;
  label: string;
  impacts: FramingImpact[];
}

const CHANGE_PREFIX = /^(look|sticker|effect|fx|sound|framing|hl|tok|caption|cap)[-_]/i;

/** "look-cool-cast" → "Cool Cast", "framing-hl-snack-stole" → "Snack Stole". */
function prettyChangeLabel(id: string): string {
  let s = String(id);
  while (CHANGE_PREFIX.test(s)) s = s.replace(CHANGE_PREFIX, "");
  s = s.replace(/[-_]/g, " ").trim();
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The player's CURRENTLY-APPLIED edits, each with its emotion impact — derived
 * from the SAME levers `editEmotionVector` folds into the landing, so the list is
 * honest ("what you changed → what it did"). Drives the editor panel's Advanced
 * "What you changed" read. Presentation only; never feeds scoring.
 */
export function changeRows(
  resolved: ResolvedEdit | null | undefined,
  config: LandingConfig,
  opts: {
    activeEffectIds?: readonly string[];
    captionPlacement?: CaptionPlacementZone | null;
    jumpCuts?: number;
  } = {},
): EditChangeRow[] {
  const rows: EditChangeRow[] = [];
  if (!resolved) return rows;
  const levers = config.levers;

  // Captions: authored framing (per token) + non-default style.
  const caps = resolved.captions ?? [];
  const seenFraming = new Set<string>();
  for (const cap of caps) {
    const style = cap.style ?? "documentary";
    if (style !== "documentary") {
      rows.push({
        key: `style-${style}`,
        label: `Caption style: ${prettyChangeLabel(style)}`,
        impacts: styleImpacts(style, config),
      });
    }
    for (const tokenId of cap.tokenIds ?? []) {
      if (seenFraming.has(tokenId)) continue;
      seenFraming.add(tokenId);
      const spec = levers.framingTokens?.[tokenId];
      if (!spec) continue;
      rows.push({
        key: `cap-${tokenId}`,
        label: `Caption: ${prettyChangeLabel(tokenId)}`,
        impacts: emotionSpecImpacts(spec, { reach: true }),
      });
    }
  }

  // Caption placement (only meaningful with a caption on the reel).
  if (opts.captionPlacement && levers.captionPlacement && caps.length > 0) {
    rows.push({
      key: `placement-${opts.captionPlacement}`,
      label: `Caption placement: ${prettyChangeLabel(String(opts.captionPlacement))}`,
      impacts: emotionSpecImpacts(levers.captionPlacement[opts.captionPlacement]),
    });
  }

  // Look / grade.
  const lookId = String(resolved.look?.treatmentId ?? "");
  if (lookId) {
    rows.push({
      key: `look-${lookId}`,
      label: `Look: ${prettyChangeLabel(lookId)}`,
      impacts: lookFilterImpacts(lookId, config),
    });
  }

  // Stackable effects.
  for (const fx of opts.activeEffectIds ?? []) {
    rows.push({
      key: `fx-${fx}`,
      label: `Effect: ${prettyChangeLabel(fx)}`,
      impacts: effectImpacts(fx, config),
    });
  }

  // Sound bed.
  for (const s of resolved.sounds ?? []) {
    const name = prettyChangeLabel(String(s.role ?? s.assetId ?? "sound"));
    rows.push({
      key: `sound-${String(s.assetId ?? s.role)}`,
      label: `Sound: ${name}`,
      impacts: soundImpacts(s, config),
    });
  }

  // Stickers: label + chips both read the SAME anchor-vs-combo resolution as
  // the landing model, so "what you changed" never overclaims a sticker's
  // effect — an on-combo placement reads "on <Zone>" with its axis chip(s);
  // any other placement (wrong zone, wrong sticker for the zone, or a legacy
  // instance with no anchor) reads plain and shows "cosmetic".
  for (const st of resolved.stickers ?? []) {
    const sid = String(st?.stickerId ?? "");
    if (!sid) continue;
    const anchor = {
      xMilli: st.anchorXMilli,
      yMilli: st.anchorYMilli,
      startTick: st.startTick,
      endTick: st.endTick,
    };
    const combo = stickerComboForPlacement(
      sid,
      anchor.xMilli,
      anchor.yMilli,
      anchor.startTick,
      anchor.endTick,
      levers.stickerZones,
      levers.stickerCombos,
    );
    const zone = combo ? levers.stickerZones.find((z) => z.id === combo.zoneId) : undefined;
    rows.push({
      key: `sticker-${st.id}-${sid}`,
      label: `Sticker: ${prettyChangeLabel(sid)}${zone ? ` (on ${zone.label})` : ""}`,
      impacts: stickerImpacts(sid, config, anchor),
    });
  }

  // Cut context (omitted beats).
  let omits = 0;
  for (const c of resolved.cuts?.values?.() ?? []) if (c?.mode === "omit") omits += 1;
  if (omits > 0) {
    rows.push({
      key: "cut-context",
      label: `Cut context (${omits} beat${omits > 1 ? "s" : ""})`,
      impacts: contextCutImpacts(config),
    });
  }

  // Jump cuts.
  if ((opts.jumpCuts ?? 0) > 0) {
    rows.push({
      key: "jump-cut",
      label: `Jump cuts (${opts.jumpCuts})`,
      impacts: jumpCutImpacts(config),
    });
  }

  return rows;
}
